import fs from 'fs/promises'
import path from 'path'
import chalk from 'chalk'
import { z } from 'zod'

interface ValidateOptions {
  schema: string
  env: string
  strict: boolean
  debug: boolean
  ci: boolean
}

export async function validate(options: ValidateOptions) {
  let { schema, env, strict, debug, ci } = options

  if (ci) {
    strict = true
    debug = false
  }

  console.log(chalk.blue('🔍 Validating environment configuration...\n'))

  try {
    // Check if schema file exists
    await fs.access(schema)
  } catch {
    throw new Error(`Schema file not found: ${schema}`)
  }

  try {
    // Check if .env file exists
    await fs.access(env)
  } catch {
    throw new Error(`Environment file not found: ${env}`)
  }

  // Load the schema
  const { loadSchemaFile } = await import('./utils')
  const schemaModule = await loadSchemaFile(schema)
  if (!schemaModule.env) {
    throw new Error(`No 'env' export found in ${schema}`)
  }

  // Create the proper Zod schema from the raw structure
  const schemaDef = schemaModule.env
  const envSchema = z.object({
    ...(schemaDef.client || {}),
    ...(schemaDef.server || {})
  })

  // Load .env file
  const envContent = await fs.readFile(env, 'utf-8')
  const envVars: Record<string, string> = {}
  
  for (const line of envContent.split('\n')) {
    const trimmed = line.trim()
    if (trimmed && !trimmed.startsWith('#')) {
      const [key, ...valueParts] = trimmed.split('=')
      if (key && valueParts.length > 0) {
        envVars[key] = valueParts.join('=')
      }
    }
  }

  console.log(chalk.gray(`📁 Schema: ${schema}`))
  console.log(chalk.gray(`📁 Environment: ${env}`))
  console.log(chalk.gray(`🔧 Strict mode: ${strict ? 'enabled' : 'disabled'}`))
  console.log(chalk.gray(`🐛 Debug mode: ${debug ? 'enabled' : 'disabled'}`))
  if (ci) console.log(chalk.gray('🏗️  CI/build mode: enabled'))
  console.log('')

  // Validate the environment variables
  try {
    const validatedEnv = envSchema.parse(envVars)
    
    console.log(chalk.green('✅ Environment validation passed!'))
    
    if (debug) {
      console.log(chalk.blue('\n📊 Validated environment variables:'))
      Object.entries(validatedEnv).forEach(([key, value]) => {
        const displayValue = typeof value === 'string' && value.length > 50 
          ? `${value.substring(0, 50)}...` 
          : value
        console.log(chalk.gray(`  ${key}: ${displayValue}`))
      })
    }

    // Check for missing required variables
    const missingVars: string[] = []
    
    if (schemaDef.client) {
      Object.keys(schemaDef.client).forEach(key => {
        if (!envVars[key] && !schemaDef.client[key]._def.defaultValue) {
          missingVars.push(key)
        }
      })
    }
    
    if (schemaDef.server) {
      Object.keys(schemaDef.server).forEach(key => {
        if (!envVars[key] && !schemaDef.server[key]._def.defaultValue) {
          missingVars.push(key)
        }
      })
    }

    if (missingVars.length > 0) {
      console.log(chalk.yellow('\n⚠️  Missing environment variables:'))
      missingVars.forEach(varName => {
        console.log(chalk.yellow(`  - ${varName}`))
      })
      console.log(chalk.gray('\nAdd these to your .env file or provide default values in your schema.'))
      if (ci) process.exit(1)
    }

  } catch (error) {
    console.log(chalk.red('❌ Environment validation failed!'))
    console.log(chalk.red('\nError details:'))
    
    if (error instanceof Error) {
      console.log(chalk.red(error.message))
    } else {
      console.log(chalk.red(String(error)))
    }
    
    process.exit(1)
  }
} 